import { api } from "./api";
import { ListRequest, MessageResponse } from "../types/common/commonTypes";
import {
  InvPosCreate,
  InvPosUpdate,
  InvPosFull,
  PaginatedInvPosResponse,
  InvPosRequest,
} from "../types/invPosTypes";

/**
 * Inventory Management Service
 *
 * Provides complete integration with all 12 inventory management API endpoints:
 * - Core lot management (CRUD operations)
 * - FIFO allocation engine
 * - Real-time availability tracking
 * - Lot shipping and movement
 * - Comprehensive inventory summaries
 *
 * Auto-generation: Lot numbers are auto-generated by backend (YY-###### format)
 * Example: 24-000001, 24-000002, etc. (resets annually)
 */
const pos_base = "/inventory/position";
export const invPositionService = {
  // ========================================
  // Core Inventory Lot Management (6 endpoints)
  // ========================================

  /**
   * Create a new inventory lot
   * POST /api/v1/inventory/lots
   * Note: Lot numbers are auto-generated by backend (format: YY-######)
   */
  create: async (data: InvPosCreate): Promise<InvPosFull> => {
    const response = await api.post(`${pos_base}/`, data);
    return response.data;
  },
  list: async (params: ListRequest): Promise<PaginatedInvPosResponse> => {
    const response = await api.post(`${pos_base}/search`, params);
    return response.data;
  },
  listByItem: async (params: ListRequest): Promise<PaginatedInvPosResponse> => {
    const response = await api.post(`${pos_base}/item`, params);
    return response.data;
  },
  listByLot: async (params: ListRequest): Promise<PaginatedInvPosResponse> => {
    const response = await api.post(`${pos_base}/lot`, params);
    return response.data;
  },
  listForFulfillment: async (
    itemId: string,
  ): Promise<PaginatedInvPosResponse> => {
    const response = await api.get(`${pos_base}/fulfillment/${itemId}`);
    return response.data;
  },
  listv2: async (request: InvPosRequest): Promise<PaginatedInvPosResponse> => {
    const response = await api.post(`${pos_base}/request`, request);
    return response.data;
  },

  /**
   * Get a specific inventory lot by ID
   * GET /api/v1/inventory/lots/{lot_id}
   */
  getById: async (id: string): Promise<InvPosFull> => {
    const response = await api.get(`${pos_base}/${id}`);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  update: async (id: string, data: InvPosUpdate): Promise<InvPosFull> => {
    const response = await api.put(`${pos_base}/${id}`, { ...data, id });
    return response.data;
  },

  /**
   * Delete an inventory lot (soft delete)
   * DELETE /api/v1/inventory/lots/{lot_id}
   */
  deactivate: async (id: string): Promise<MessageResponse> => {
    const response = await api.delete(`${pos_base}/${id}`);
    return response.data;
  },

  /**
   * Restore a deleted inventory lot
   * POST /api/v1/inventory/lots/{lot_id}/restore
   */
  reactivate: async (id: string): Promise<InvPosFull> => {
    const response = await api.post(`${pos_base}/${id}/restore`);
    return response.data;
  },
};
